import React, { useState, useEffect, useMemo } from 'react'
import {
  ModulePanel,
  ModuleHeader,
  ModuleBody,
  ModuleFooter
} from 'containers/purchase/modules/module_panel'
import { Row, Col, Card, CardBody, CardTitle } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import ProductVariantCard from './product_variant_card'
import languagesData from 'i18n/languages'
import { CircleFlag } from 'react-circle-flags'

const ProductVariantLanguageCard = ({ language, selected, onSelect }) => {
  const languageData = languagesData[language]

  return (
    <Card
      onClick={onSelect}
      className={`w-100 cursor-pointer ${
        selected ? 'border-primary shadow-sm' : ''
      }`}
      role="button"
    >
      <CardBody className="text-center">
        <CardTitle tag="h5" className="mb-0">
          <CircleFlag countryCode={languageData ? languageData.flag_code : language} height="23" className="me-2" />
          {languageData ? languageData.label : language}
        </CardTitle>
      </CardBody>
    </Card>
  )
}

const ProductVariantsModule = (props) => {
  const { config, machine, status } = props
  const { i18n } = useTranslation()
  const [current, send] = machine
  const { locale } = config
  const { product_variants = [], product_variant_id } = current.context

  const languages = useMemo(() => {
    return [
      ...new Set(
        product_variants
          .map((variant) => variant.language)
          .filter(Boolean)
      )
    ]
  }, [product_variants])

  // console.log(languagesData)

  const hasMultipleLanguages = languages.length > 1

  const [selectedLanguage, setSelectedLanguage] = useState(null)

  useEffect(() => {
    if (hasMultipleLanguages && !selectedLanguage) {
      setSelectedLanguage(languages[0])
    }

    if (!hasMultipleLanguages) {
      setSelectedLanguage(null)
    }
  }, [hasMultipleLanguages, languages, selectedLanguage])

  const visibleVariants = useMemo(() => {
    if (!hasMultipleLanguages || !selectedLanguage) {
      return product_variants
    }

    return product_variants.filter(
      (variant) => variant.language === selectedLanguage
    )
  }, [product_variants, hasMultipleLanguages, selectedLanguage])

  const variantSelect = (variant) => {
    send({ type: 'PRODUCT_VARIANT_SELECTION', variant })
  }

  const selectedVariant = () => {
    if (product_variants && product_variant_id) {
      return product_variants.find((variant) => variant.id == product_variant_id)
    }

    return null
  }


  return (
    <ModulePanel>
      <ModuleHeader
        status={status}
        onClick={() =>
          status === 'completed' ? send('REOPEN_PRODUCTVARIANTSSTEP') : null
        }
      >
        <div>
          <i className="fas fa-grip-horizontal me-2" aria-hidden="true"></i>
          {status === 'completed'
            ? selectedVariant()?.name
            : i18n.t('product_variants_module.title')}
        </div>
      </ModuleHeader>

      <ModuleBody status={status}>
        {hasMultipleLanguages && (
          <React.Fragment>
            <Row>
              <Col>
                <div className="h5 text-center">
                  {i18n.t('product_variants_module.language_selector_label')}
                </div>
              </Col>
            </Row>

            <Row className="row-cols-1 row-cols-sm-2 row-cols-md-4 p-3 justify-content-center g-3">
              {languages.map((language) => (
                <Col key={language} className="d-flex">
                  <ProductVariantLanguageCard
                    language={language}
                    selected={selectedLanguage === language}
                    onSelect={() => setSelectedLanguage(language)}
                  />
                </Col>
              ))}
            </Row>
          </React.Fragment>
        )}

        {hasMultipleLanguages &&
          <Row>
            <Col>
              <div className="h5 text-center">
                {i18n.t('product_variants_module.variant_select_title')}
              </div>
            </Col>
          </Row>          
        }

        <Row className="row-cols-1 row-cols-sm-2 row-cols-md-4 p-3 justify-content-center g-3">
          {visibleVariants.map((variant) => {
            const {
              id,
              name,
              description,
              featured_price_label,
              thumbnail
            } = variant

            return (
              <Col key={id} className="d-flex">
                <ProductVariantCard
                  id={id}
                  title={name}
                  description={description}
                  featured_price_label={featured_price_label}
                  thumbnail={thumbnail}
                  onSelect={() => variantSelect(variant)}
                />
              </Col>
            )
          })}
        </Row>
      </ModuleBody>

      <ModuleFooter {...props} />
    </ModulePanel>
  )
}

export default ProductVariantsModule
